import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Paper, Box, Button, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Snackbar, Alert
} from '@mui/material';
import { 
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const ScheduleXmlTemplateList = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [xmlFile, setXmlFile] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', content: '', templateId: null, templateName: '' });
  const { getAuthHeaders, hasPermission } = useAuth();

  // Jogosultság ellenőrzés - sablon feltöltéshez kell a canUploadTemplates jogosultság
  const canEdit = hasPermission('canUploadTemplates');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/schedule-xml/templates', { headers: getAuthHeaders() });
      setTemplates(response.data);
    } catch (err) {
      console.error('Error loading templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = (id, name) => {
    setConfirmDialog({
      open: true,
      title: 'Sablon törlése',
      content: `Biztosan törölni szeretné a "${name}" sablont?`,
      templateId: id,
      templateName: name
    });
  };

  const confirmDeleteTemplate = async () => {
    const { templateId } = confirmDialog;
    
    try {
      await axios.delete(`/api/schedule-xml/templates/${templateId}`, { headers: getAuthHeaders() });
      loadTemplates();
      setSnackbar({ open: true, message: 'Sablon sikeresen törölve!', severity: 'success' });
    } catch (err) {
      console.error('Delete template error:', err);
      setSnackbar({ open: true, message: err.response?.data?.message || 'Hiba a sablon törlésekor', severity: 'error' });
    } finally {
      setConfirmDialog({ ...confirmDialog, open: false });
    }
  };

  const handleXmlUpload = async () => {
    if (!xmlFile || !templateName) return;
    
    try {
      const formData = new FormData();
      formData.append('file', xmlFile);
      formData.append('name', templateName);
      formData.append('description', templateDescription);
      formData.append('day', new Date().toISOString().split('T')[0]);
      
      await axios.post('/api/schedule-xml/templates/upload', formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setShowUploadDialog(false);
      setSelectedTemplate(null);
      setXmlFile(null);
      setTemplateName('');
      setTemplateDescription('');
      loadTemplates();
      setSnackbar({ open: true, message: 'Sablon sikeresen feltöltve!', severity: 'success' });
    } catch (err) {
      console.error('Template upload error:', err);
      setSnackbar({ open: true, message: err.response?.data?.message || 'Hiba a sablon feltöltésekor', severity: 'error' });
    }
  };

  const getDayTypeLabel = (dayType) => {
    switch (dayType) {
      case 'workday': return 'Munkanap';
      case 'saturday': return 'Szombat';
      case 'sunday': return 'Vasárnap';
      case 'holiday': return 'Ünnepnap';
      case 'custom': return 'Egyedi';
      default: return dayType;
    }
  };

  const getDayTypeColor = (dayType) => {
    switch (dayType) {
      case 'workday': return 'primary';
      case 'saturday': return 'secondary';
      case 'sunday': return 'error';
      case 'holiday': return 'error';
      case 'custom': return 'default';
      default: return 'default';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Menetrend sablonok
          </Typography>
          <Box>
            <Button
              variant="outlined"
              component={RouterLink}
              to="/schedule-xml"
              sx={{ mr: 1 }}
            >
              Vissza a naptárhoz
            </Button>
            {canEdit && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setSelectedTemplate(null);
                  setTemplateName('');
                  setTemplateDescription('');
                  setXmlFile(null);
                  setShowUploadDialog(true);
                }}
                startIcon={<AddIcon />}
              >
                Új sablon
              </Button>
            )}
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Név</TableCell>
                <TableCell>Típus</TableCell>
                <TableCell>Leírás</TableCell>
                <TableCell>Feltöltve</TableCell>
                <TableCell>Frissítve</TableCell>
                <TableCell align="right">Műveletek</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Nincsenek sablonok. Hozzon létre új sablont a "+" gombbal.
                  </TableCell>
                </TableRow>
              ) : (
                templates.map(template => (
                  <TableRow key={template.id}>
                    <TableCell>{template.name}</TableCell>
                    <TableCell>
                      <Chip 
                        label={getDayTypeLabel(template.dayType)} 
                        color={getDayTypeColor(template.dayType)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{template.description || '-'}</TableCell>
                    <TableCell>{new Date(template.createdAt).toLocaleDateString('hu-HU')}</TableCell>
                    <TableCell>{new Date(template.updatedAt).toLocaleDateString('hu-HU')}</TableCell>
                    <TableCell align="right">
                      {canEdit && (
                        <IconButton 
                          color="error"
                          onClick={() => handleDeleteTemplate(template.id, template.name)}
                          title="Törlés"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Typography>Betöltés...</Typography>
          </Box>
        )}
      </Paper>

      {/* XML feltöltés dialógus */}
      <Dialog 
        open={showUploadDialog} 
        onClose={() => {
          setShowUploadDialog(false);
          setSelectedTemplate(null);
          setXmlFile(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Új sablon feltöltése</DialogTitle>
        <DialogContent>
          <Box sx={{ my: 2 }}>
            <TextField
              label="Sablon neve"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            
            <TextField
              label="Leírás"
              value={templateDescription}
              onChange={(e) => setTemplateDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
            
            <TextField
              type="file"
              inputProps={{ accept: '.xml,.csv' }}
              onChange={(e) => setXmlFile(e.target.files[0])}
              fullWidth
              required
              helperText="Válasszon ki egy XML vagy CSV fájlt (FordajegyzékGroupHeader formátum)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowUploadDialog(false);
              setSelectedTemplate(null);
              setXmlFile(null);
              setTemplateName('');
              setTemplateDescription('');
            }}
          >
            Mégsem
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleXmlUpload}
            disabled={!xmlFile || !templateName}
          >
            Feltöltés
          </Button>
        </DialogActions>
      </Dialog>

      {/* Megerősítő dialógus */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        PaperProps={{
          elevation: 8,
          sx: {
            borderRadius: 2,
            p: 1,
            minWidth: '350px',
            maxWidth: '450px'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'warning.main', 
          color: 'warning.contrastText', 
          borderRadius: '8px 8px 0 0',
          py: 1.5
        }}>
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent sx={{ py: 3, px: 3 }}>
          <Typography variant="body1">{confirmDialog.content}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button 
            variant="outlined" 
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            sx={{ minWidth: '100px' }}
          >
            Mégse
          </Button>
          <Button 
            variant="contained" 
            color="warning"
            onClick={confirmDeleteTemplate}
            sx={{ minWidth: '100px' }}
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar értesítések */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 9999 }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
          elevation={6}
          sx={{ width: '100%', fontSize: '1.1rem' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ScheduleXmlTemplateList;
